/**
 * Session cache for GET /clubs/ and GET /clubs/:id/.
 * Fresh for the tab session until a write or logout invalidates it.
 */

export const USER_CLUBS_KEY = 'user:clubs';
export const CLUBS_LIST_PATH = '/clubs/';

export function clubDetailKey(clubId) {
  return `club:${clubId}:detail`;
}

export function clubDetailPath(clubId) {
  return `/clubs/${clubId}/`;
}

const memory = {
  list: null,
  details: new Map(),
};

let inFlightList = null;
const inFlightDetails = new Map();

export function isValidClub(data) {
  return Boolean(data) && typeof data === 'object' && !Array.isArray(data) && data.id != null;
}

export function isValidClubList(data) {
  return Array.isArray(data) && data.every(isValidClub);
}

function readStorageEntry(key, isValid) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !isValid(parsed.data)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStorageEntry(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // quota / private mode
  }
}

function writeDetail(club) {
  const id = String(club.id);
  memory.details.set(id, { data: club, ts: Date.now() });
  writeStorageEntry(clubDetailKey(club.id), club);
}

function writeList(list) {
  memory.list = { data: list, ts: Date.now() };
  writeStorageEntry(USER_CLUBS_KEY, list);
}

export function getCachedClubs() {
  if (memory.list && isValidClubList(memory.list.data)) return memory.list.data;
  const stored = readStorageEntry(USER_CLUBS_KEY, isValidClubList);
  if (!stored) return null;
  memory.list = stored;
  return stored.data;
}

export function isCachedClubsFresh() {
  return getCachedClubs() != null;
}

export function setCachedClubs(list) {
  if (!isValidClubList(list)) return;
  writeList(list);
}

export function getCachedClub(clubId) {
  if (clubId == null || clubId === '') return null;
  const id = String(clubId);
  const mem = memory.details.get(id);
  if (mem && isValidClub(mem.data)) return mem.data;
  const stored = readStorageEntry(clubDetailKey(clubId), isValidClub);
  if (!stored) return null;
  memory.details.set(id, stored);
  return stored.data;
}

export function isCachedClubFresh(clubId) {
  return getCachedClub(clubId) != null;
}

export function setCachedClub(club) {
  if (!isValidClub(club)) return;
  writeDetail(club);
  const list = getCachedClubs();
  if (!list) return;
  const id = String(club.id);
  const idx = list.findIndex((row) => String(row.id) === id);
  const next = idx >= 0
    ? list.map((row, i) => (i === idx ? { ...row, ...club } : row))
    : [...list, club];
  writeList(next);
}

export function removeCachedClub(clubId) {
  if (clubId == null || clubId === '') return;
  const id = String(clubId);
  memory.details.delete(id);
  inFlightDetails.delete(id);
  try {
    sessionStorage.removeItem(clubDetailKey(clubId));
  } catch {
    // ignore
  }
  const list = getCachedClubs();
  if (list) writeList(list.filter((row) => String(row.id) !== id));
}

export function invalidateCachedClubsList() {
  memory.list = null;
  inFlightList = null;
  try {
    sessionStorage.removeItem(USER_CLUBS_KEY);
  } catch {
    // ignore
  }
}

export function invalidateClubsCache() {
  memory.list = null;
  memory.details.clear();
  inFlightList = null;
  inFlightDetails.clear();
  try {
    sessionStorage.removeItem(USER_CLUBS_KEY);
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('club:') && key.endsWith(':detail')) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

export function fetchClubs(client, { force = false } = {}) {
  if (!client || typeof client.get !== 'function') {
    return Promise.reject(new Error('api client required'));
  }
  if (!force) {
    const fresh = getCachedClubs();
    if (fresh) return Promise.resolve({ data: fresh });
  }
  if (inFlightList) return inFlightList;

  inFlightList = client
    .get(CLUBS_LIST_PATH)
    .then((res) => {
      if (isValidClubList(res?.data)) setCachedClubs(res.data);
      return res;
    })
    .finally(() => {
      inFlightList = null;
    });

  return inFlightList;
}

export function fetchClub(client, clubId, { force = false } = {}) {
  if (!client || typeof client.get !== 'function') {
    return Promise.reject(new Error('api client required'));
  }
  if (clubId == null || clubId === '') {
    return Promise.reject(new Error('club id required'));
  }
  const id = String(clubId);
  if (!force) {
    const fresh = getCachedClub(clubId);
    if (fresh) return Promise.resolve({ data: fresh });
  }
  if (inFlightDetails.has(id)) return inFlightDetails.get(id);

  const pending = client
    .get(clubDetailPath(clubId))
    .then((res) => {
      if (isValidClub(res?.data)) setCachedClub(res.data);
      return res;
    })
    .finally(() => {
      inFlightDetails.delete(id);
    });

  inFlightDetails.set(id, pending);
  return pending;
}
